import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
} from '../utils/webComponents';
import { 
  School, 
  Class, 
  ArrowForward, 
  Add, 
  Close, 
  Check
} from '@mui/icons-material';
import { useFlashcards } from '../context/FlashcardContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';

const OnboardingScreen = ({ isAddingClasses = false, onComplete }) => {
  const { setUser, setOnboarded, addClass, user } = useFlashcards();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [userName, setUserName] = useState(isAddingClasses ? (user?.name || '') : '');
  const [classes, setClasses] = useState([{ name: '', color: '#60A5FA' }]);
  const [currentStep, setCurrentStep] = useState(isAddingClasses ? 1 : 0);

  const classColors = [
    '#60A5FA', '#34D399', '#FBBF24', '#F87171', 
    '#A78BFA', '#22D3EE', '#86EFAC', '#94A3B8'
  ];

  const addNewClass = () => {
    if (classes.length < 8) {
      setClasses([...classes, { name: '', color: classColors[classes.length % classColors.length] }]);
    }
  };

  const removeClass = (index) => {
    if (classes.length > 1) {
      setClasses(classes.filter((_, i) => i !== index));
    }
  };

  const updateClass = (index, field, value) => {
    const updatedClasses = [...classes];
    updatedClasses[index] = { ...updatedClasses[index], [field]: value };
    setClasses(updatedClasses);
  };

  const handleNext = () => {
    if (currentStep === 0 && userName.trim()) {
      setCurrentStep(1);
    }
  };

  const handleFinish = async () => {
    try {
      const validClasses = classes.filter(cls => cls.name.trim());
      
      if (validClasses.length === 0) {
        return;
      }

      if (!isAddingClasses) {
        setUser({
          name: userName.trim(),
          isOnboarded: true,
          streak: 0,
          totalStudyTime: 0,
          lastStudyDate: null,
          preferences: {
            sessionLength: 20,
            difficulty: 'medium',
            notifications: true,
          },
        });
        setOnboarded(true);
      }

      validClasses.forEach(classData => {
        addClass({
          name: classData.name.trim(),
          color: classData.color,
        });
      });

      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error in handleFinish:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (currentStep === 0) {
        handleNext();
      } else {
        handleFinish();
      }
    }
  };

  // Step 1: Name Input
  const renderStep1 = () => (
    <View style={styles.content}>
      <View style={[styles.logoContainer, { backgroundColor: theme.primary }]}>
        <School sx={{ fontSize: 32, color: 'white' }} />
      </View>

      <Text style={[styles.title, { color: theme.text }]}>{t('onboarding.welcomeToSpacedAI')}</Text>
      <Text style={[styles.subtitle, { color: theme.textSecondary }]}>
        {t('onboarding.letsGetYouStarted')}
      </Text>

      <View style={styles.form}>
        <View style={styles.inputWrapper}>
          <View style={[styles.inputContainer, { backgroundColor: theme.inputBackground, borderColor: theme.inputBorder }]}>
            <TextInput
              style={[styles.input, { color: theme.inputText }]}
              placeholder={t('onboarding.enterYourName')}
              value={userName}
              onChangeText={setUserName}
              onKeyPress={handleKeyPress}
              autoFocus
            />
          </View>
        </View>

        <TouchableOpacity
          style={[
            styles.nextButton,
            { 
              backgroundColor: userName.trim() ? theme.primary : theme.textMuted
            }
          ]}
          onPress={handleNext}
          disabled={!userName.trim()}
        >
          <Text style={[styles.nextButtonText, { color: theme.buttonText }]}>{t('onboarding.continue')}</Text>
          <ArrowForward sx={{ fontSize: 20, color: theme.buttonText }} />
        </TouchableOpacity>
      </View>
    </View>
  );

  // Step 2: Add Classes
  const renderStep2 = () => {
    const validClasses = classes.filter(cls => cls.name.trim());
    const canFinish = validClasses.length > 0;

    return (
      <View style={styles.content}>
        <View style={[styles.logoContainer, { backgroundColor: theme.success }]}>
          <Class sx={{ fontSize: 32, color: 'white' }} />
        </View>

        <Text style={[styles.title, { color: theme.text }]}>{t('onboarding.addClasses')}</Text>
        <Text style={[styles.subtitle, { color: theme.textSecondary }]}>
          {t('onboarding.addClassesSubtitle')}
        </Text>

        <View style={styles.form}>
          <View style={styles.classesList}>
            {classes.map((cls, index) => (
              <View 
                key={`class-${index}-${cls.color}`} 
                style={[styles.classItem, { 
                  backgroundColor: theme.inputBackground, 
                  borderColor: theme.inputBorder 
                }]}
              >
                <View style={[styles.colorIndicator, { backgroundColor: cls.color }]} />
                <TextInput
                  style={[styles.classInput, { color: theme.inputText }]}
                  value={cls.name}
                  onChangeText={(text) => updateClass(index, 'name', text)}
                  onKeyPress={handleKeyPress}
                  placeholder={t('onboarding.className')}
                />
                {classes.length > 1 && (
                  <TouchableOpacity
                    style={styles.removeButton}
                    onPress={() => removeClass(index)}
                  >
                    <Close sx={{ fontSize: 18, color: theme.error }} />
                  </TouchableOpacity>
                )}
              </View>
            ))}
            
            {classes.length < 8 && (
              <TouchableOpacity 
                style={[styles.addClassButton, { 
                  backgroundColor: theme.success, 
                  borderColor: theme.success 
                }]} 
                onPress={addNewClass}
              >
                <Add sx={{ fontSize: 20, color: theme.buttonText }} />
                <Text style={[styles.addClassText, { color: theme.buttonText }]}>{t('onboarding.addAnotherClass')}</Text>
              </TouchableOpacity>
            )}
          </View>

          <TouchableOpacity
            style={[
              styles.finishButton,
              { 
                backgroundColor: canFinish ? theme.success : theme.textMuted
              }
            ]}
            onPress={handleFinish}
            disabled={!canFinish}
          >
            <Text style={[styles.finishButtonText, { color: theme.buttonText }]}>
              {isAddingClasses ? t('common.done') : t('onboarding.completeSetup')}
            </Text>
            <Check sx={{ fontSize: 20, color: theme.buttonText }} />
          </TouchableOpacity>

          {isAddingClasses && (
            <TouchableOpacity
              style={[styles.cancelButton, { borderColor: theme.border }]}
              onPress={onComplete}
            >
              <Text style={[styles.cancelButtonText, { color: theme.textSecondary }]}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      {!isAddingClasses && (
        <View style={styles.progressContainer}>
          <View style={[styles.progressBar, { backgroundColor: theme.border }]}>
            <View 
              style={[
                styles.progressFill, 
                { 
                  backgroundColor: theme.primary,
                  width: `${((currentStep + 1) / 2) * 100}%`
                }
              ]} 
            />
          </View>
        </View>
      )}
      
      {currentStep === 0 ? renderStep1() : renderStep2()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  progressContainer: {
    paddingHorizontal: 24,
    paddingTop: 20,
    marginBottom: 20,
  },
  progressBar: {
    height: 4,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
    transition: 'width 0.3s',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    maxWidth: 500,
    width: '100%',
    margin: '0 auto',
  },
  logoContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 32,
    textAlign: 'center',
  },
  form: {
    width: '100%',
  },
  inputWrapper: {
    marginBottom: 24,
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  input: {
    flex: 1,
    fontSize: 16,
    border: 'none',
    outline: 'none',
    background: 'transparent',
  },
  nextButton: {
    width: '100%',
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  nextButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  classesList: {
    width: '100%',
    marginBottom: 24,
    maxHeight: 400,
    overflowY: 'auto',
  },
  classItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
    gap: 12,
  },
  colorIndicator: {
    width: 16,
    height: 16,
    borderRadius: 8,
  },
  classInput: {
    flex: 1,
    fontSize: 16,
    border: 'none',
    outline: 'none',
    background: 'transparent',
  },
  removeButton: {
    padding: 4,
    cursor: 'pointer',
  },
  addClassButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    padding: 14,
    borderWidth: 2,
    borderStyle: 'dashed',
    marginTop: 4,
    gap: 8,
    cursor: 'pointer',
  },
  addClassText: {
    fontSize: 16,
    fontWeight: '600',
  },
  finishButton: {
    width: '100%',
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  finishButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  cancelButton: {
    width: '100%',
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,
    marginTop: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default OnboardingScreen;
